import {
  MARKET_COUNTRIES,
  MARKET_COUNTRY_STORAGE_KEY,
  type MarketCountry,
} from "./marketCountry";

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function isAllowedMarketCountry(value: string): value is MarketCountry {
  return (MARKET_COUNTRIES as readonly string[]).includes(value);
}

function resolveCookieDomain(): string | null {
  try {
    const configured = (import.meta.env.VITE_LOCALE_COOKIE_DOMAIN as string | undefined)?.trim();
    return configured && configured.length > 0 ? configured : null;
  } catch {
    return null;
  }
}

function isSecureContext(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.location.protocol === "https:";
}

function parseMarketCountryCookie(rawCookie: string): MarketCountry | null {
  const parts = rawCookie.split(";");
  for (const part of parts) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf("=");
    if (eq <= 0) {
      continue;
    }

    const name = trimmed.slice(0, eq).trim();
    if (name !== MARKET_COUNTRY_STORAGE_KEY) {
      continue;
    }

    const value = decodeURIComponent(trimmed.slice(eq + 1).trim());
    if (isAllowedMarketCountry(value)) {
      return value;
    }
    return null;
  }

  return null;
}

export function readMarketCountryCookie(): MarketCountry | null {
  if (typeof document === "undefined") {
    return null;
  }

  try {
    return parseMarketCountryCookie(document.cookie);
  } catch {
    return null;
  }
}

export function writeMarketCountryCookie(marketCountry: MarketCountry): void {
  if (typeof document === "undefined") {
    return;
  }

  const domain = resolveCookieDomain();
  if (!domain || !isAllowedMarketCountry(marketCountry)) {
    return;
  }

  const segments = [
    `${MARKET_COUNTRY_STORAGE_KEY}=${encodeURIComponent(marketCountry)}`,
    "Path=/",
    `Max-Age=${ONE_YEAR_SECONDS}`,
    "SameSite=Lax",
  ];
  if (isSecureContext()) {
    segments.push("Secure");
  }
  segments.push(`Domain=${domain}`);

  try {
    document.cookie = segments.join("; ");
  } catch {
    /* ignore private mode / blocked cookies */
  }
}

export function clearMarketCountryCookie(): void {
  if (typeof document === "undefined") {
    return;
  }

  const domain = resolveCookieDomain();
  if (!domain) {
    return;
  }

  const segments = [
    `${MARKET_COUNTRY_STORAGE_KEY}=`,
    "Path=/",
    "Max-Age=0",
    "SameSite=Lax",
    `Domain=${domain}`,
  ];
  if (isSecureContext()) {
    segments.push("Secure");
  }

  try {
    document.cookie = segments.join("; ");
  } catch {
    /* ignore */
  }
}
